/*
 * CAPTURE-OWNED MODULE
 * Identifier Modal Component for capture-ui
 * Modal for creating / editing a capture_template_identifier.
 */

import React, { useState } from 'react';
import { createIdentifier, updateIdentifier } from '../api/captureTemplates';
import './IdentifierModal.css';

const WEIGHT_MAP = {
  minor: 0.5,
  normal: 1.0,
  critical: 2.0,
  essential: 3.0,
};

function weightToLevel(w) {
  const n = Number(w);
  if (n <= 0.5) return 'minor';
  if (n <= 1.0) return 'normal';
  if (n <= 2.0) return 'critical';
  return 'essential';
}

const TYPE_HINTS = {
  vendor_name: 'Company or vendor name (e.g., "ABC Company")',
  header_text: 'Text in document header (e.g., "TAX INVOICE")',
  footer_text: 'Text in document footer (e.g., "ABN: 12 345 678 901")',
  logo_text: 'Text visible in or near logo',
  custom: 'Any other unique text pattern',
};

const MODE_HINTS = {
  contains: 'Document must contain this text anywhere (case-insensitive)',
  exact: 'Document text must match exactly (case-insensitive)',
  starts_with: 'Document text must start with this pattern',
  regex: 'Match using regular expression pattern',
};

const IdentifierModal = ({ templateId, identifier, onClose, prefillValue }) => {
  const isEditing = !!identifier;

  const [formData, setFormData] = useState({
    identifier_type: identifier?.identifier_type || 'vendor_name',
    identifier_value: prefillValue || identifier?.identifier_value || '',
    match_mode: identifier?.match_mode || 'contains',
    weight: identifier?.weight ?? 1.0,
    is_required: identifier?.is_required || false,
    description: identifier?.description || '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.identifier_value.trim()) {
      setError('Identifier value is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditing) {
        await updateIdentifier(templateId, identifier.id, formData);
      } else {
        await createIdentifier(templateId, formData);
      }

      onClose(true);
    } catch (err) {
      console.error('[identifier-modal] Save failed:', err);
      setError(err.response?.data?.error || 'Failed to save identifier');
    } finally {
      setSaving(false);
    }
  };

  const importanceLevel = weightToLevel(formData.weight);

  return (
    <div className="idm-overlay" onClick={() => onClose(false)}>
      <div className="idm-content" onClick={(e) => e.stopPropagation()}>
        <div className="idm-header">
          <h3>{isEditing ? 'Edit' : 'Add'} Template Identifier</h3>
          <button className="idm-close" onClick={() => onClose(false)}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="idm-body">
            {error && <div className="idm-error">{error}</div>}

            {/* Identifier Type */}
            <div className="idm-group">
              <label>Identifier Type</label>
              <select
                value={formData.identifier_type}
                onChange={(e) => handleChange('identifier_type', e.target.value)}
                className="idm-select"
              >
                <option value="vendor_name">Vendor Name</option>
                <option value="header_text">Header Text</option>
                <option value="footer_text">Footer Text</option>
                <option value="logo_text">Logo Text</option>
                <option value="custom">Custom Pattern</option>
              </select>
              <small className="idm-hint">{TYPE_HINTS[formData.identifier_type]}</small>
            </div>

            {/* Identifier Value */}
            <div className="idm-group">
              <label>Text to Match <span className="idm-req">*</span></label>
              <input
                type="text"
                value={formData.identifier_value}
                onChange={(e) => handleChange('identifier_value', e.target.value)}
                className="idm-input"
                placeholder="e.g., ABC Company"
                required
                autoFocus
              />
              <small className="idm-hint">
                The exact text that should appear in documents using this template
              </small>
            </div>

            {/* Match Mode */}
            <div className="idm-group">
              <label>Match Mode</label>
              <select
                value={formData.match_mode}
                onChange={(e) => handleChange('match_mode', e.target.value)}
                className="idm-select"
              >
                <option value="contains">Contains (default)</option>
                <option value="exact">Exact Match</option>
                <option value="starts_with">Starts With</option>
                <option value="regex">Regular Expression</option>
              </select>
              <small className="idm-hint">{MODE_HINTS[formData.match_mode]}</small>
            </div>

            {/* Importance (Weight) */}
            <div className="idm-group">
              <label>Importance</label>
              <div className="idm-importance">
                {['minor', 'normal', 'critical', 'essential'].map(level => (
                  <label key={level} className="idm-imp-option">
                    <input
                      type="radio"
                      name="importance"
                      value={level}
                      checked={importanceLevel === level}
                      onChange={() => handleChange('weight', WEIGHT_MAP[level])}
                    />
                    <span className={`idm-imp-label idm-imp-${level}`}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
              <small className="idm-hint">
                Higher importance = more weight in matching score
              </small>
            </div>

            {/* Required Checkbox */}
            <div className="idm-group">
              <label className="idm-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => handleChange('is_required', e.target.checked)}
                />
                <span>This identifier is required</span>
              </label>
              <small className="idm-hint">
                If checked, documents MUST contain this text to match this template
              </small>
            </div>

            {/* Description */}
            <div className="idm-group">
              <label>Notes (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="idm-textarea"
                rows="2"
                placeholder="e.g., This ABN number always appears in their footer"
              />
            </div>
          </div>

          <div className="idm-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditing ? 'Update Identifier' : 'Add Identifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IdentifierModal;
